/**
 * Animal qualification matrix — ethical KE/momentum thresholds for the
 * North America 29 + Africa 28 + a handful of common Eurasian / Oceania
 * species. Used by the "Cleared for" panel in Wizard mode and the modal in
 * Sketch mode.
 *
 * Threshold sourcing (in priority order):
 *   1. State DNR / Fish-and-Game minimum legal poundage where stated as
 *      ft·lb (Wisconsin DNR, Pennsylvania PGC, Colorado CPW).
 *   2. Pope & Young Club ethical-shot guidance (P&Y archery sections).
 *   3. Safari Club International (SCI) penetration tables for African species.
 *   4. Easton's "minimum bow weight" charts where backed by chrono data.
 *   5. Field experience consensus from the bowhunting / safari guide community
 *      where no published threshold exists.
 *
 * Two numbers per species:
 *   keMinFtLb — kinetic energy minimum for ethical broadhead penetration.
 *   momentumMin — m·v in slug·fps; better predictor than KE alone for heavy
 *                 game with thick hide/bone (elk, moose, bears, all African).
 *
 * "warn" threshold = 110% of the minimum (a 10% buffer for marginal shots).
 *
 * Sources cited inline are deliberate — when an archnerd questions a verdict
 * we want to be able to point at the document, not handwave.
 */

export type Continent = 'north-america' | 'africa' | 'eurasia' | 'oceania';

export type AnimalClass = 'small' | 'medium' | 'large' | 'thick-skinned' | 'dangerous';

export interface Animal {
  /** Common name. */
  name: string;
  /** Scientific or descriptive (e.g. "P&Y minimum elk"). */
  latinOrNote?: string;
  continent: Continent;
  animalClass: AnimalClass;
  /** Kinetic energy minimum, ft·lb, for ethical broadhead penetration. */
  keMinFtLb: number;
  /** Momentum minimum, slug·fps. */
  momentumMin: number;
  /** Short source citation. */
  source: string;
}

/**
 * Animals organized for the modal: continent → class → species.
 * Ordering within a class follows roughly size order so users can scan.
 */
export const ANIMALS: Animal[] = [
  // ───────────────────────────────────────────────────────────────────────
  // NORTH AMERICA — the canonical "NA 29" + a couple extras
  // ───────────────────────────────────────────────────────────────────────
  // Small / thin-skinned
  { name: 'Wild turkey',          continent: 'north-america', animalClass: 'small',  keMinFtLb: 25, momentumMin: 0.35, source: 'NWTF ethical-shot guidance' },
  { name: 'Javelina',             continent: 'north-america', animalClass: 'small',  keMinFtLb: 25, momentumMin: 0.35, source: 'P&Y entry minimum' },
  { name: 'Coyote',               continent: 'north-america', animalClass: 'small',  keMinFtLb: 20, momentumMin: 0.30, source: 'P&Y minimum' },
  { name: 'Bobcat',               continent: 'north-america', animalClass: 'small',  keMinFtLb: 20, momentumMin: 0.30, source: 'P&Y minimum' },
  { name: 'Pronghorn antelope',   continent: 'north-america', animalClass: 'small',  keMinFtLb: 25, momentumMin: 0.35, source: 'P&Y · WGFD' },
  { name: 'Wolf',                 continent: 'north-america', animalClass: 'medium', keMinFtLb: 35, momentumMin: 0.40, source: 'P&Y · ADFG' },

  // Medium / standard deer-class
  { name: 'Whitetail deer',       continent: 'north-america', animalClass: 'medium', keMinFtLb: 25, momentumMin: 0.35, source: 'Wisconsin DNR · P&Y' },
  { name: 'Mule deer',            continent: 'north-america', animalClass: 'medium', keMinFtLb: 30, momentumMin: 0.40, source: 'CPW guidance' },
  { name: 'Sitka blacktail',      continent: 'north-america', animalClass: 'medium', keMinFtLb: 30, momentumMin: 0.40, source: 'ADFG' },
  { name: 'Columbia blacktail',   continent: 'north-america', animalClass: 'medium', keMinFtLb: 30, momentumMin: 0.40, source: 'ODFW' },
  { name: 'Coues whitetail',      continent: 'north-america', animalClass: 'medium', keMinFtLb: 25, momentumMin: 0.35, source: 'AZGFD' },
  { name: 'Caribou (mountain)',   continent: 'north-america', animalClass: 'medium', keMinFtLb: 40, momentumMin: 0.45, source: 'P&Y · ADFG' },
  { name: 'Caribou (woodland)',   continent: 'north-america', animalClass: 'medium', keMinFtLb: 40, momentumMin: 0.45, source: 'P&Y · ADFG' },
  { name: 'Caribou (barren-ground)', continent: 'north-america', animalClass: 'medium', keMinFtLb: 40, momentumMin: 0.45, source: 'P&Y · ADFG' },
  { name: 'Caribou (Quebec-Labrador)', continent: 'north-america', animalClass: 'medium', keMinFtLb: 40, momentumMin: 0.45, source: 'P&Y' },
  { name: 'Caribou (central Canada)', continent: 'north-america', animalClass: 'medium', keMinFtLb: 40, momentumMin: 0.45, source: 'P&Y' },
  { name: 'Mountain goat',        continent: 'north-america', animalClass: 'medium', keMinFtLb: 45, momentumMin: 0.45, source: 'P&Y · MDFWP' },
  { name: 'Bighorn sheep',        continent: 'north-america', animalClass: 'medium', keMinFtLb: 45, momentumMin: 0.50, source: 'P&Y · WGFD' },
  { name: "Dall's sheep",         continent: 'north-america', animalClass: 'medium', keMinFtLb: 45, momentumMin: 0.50, source: 'P&Y · ADFG' },
  { name: "Stone's sheep",        continent: 'north-america', animalClass: 'medium', keMinFtLb: 45, momentumMin: 0.50, source: 'P&Y · BC' },
  { name: 'Desert sheep',         continent: 'north-america', animalClass: 'medium', keMinFtLb: 45, momentumMin: 0.50, source: 'P&Y · AZGFD' },

  // Large / heavy
  { name: 'Black bear',           continent: 'north-america', animalClass: 'large',  keMinFtLb: 45, momentumMin: 0.50, source: 'P&Y · Easton Spec' },
  { name: 'Elk',                  continent: 'north-america', animalClass: 'large',  keMinFtLb: 65, momentumMin: 0.55, source: 'CPW · P&Y' },
  { name: 'Moose (Shiras)',       continent: 'north-america', animalClass: 'large',  keMinFtLb: 75, momentumMin: 0.60, source: 'P&Y · MDFWP' },
  { name: 'Moose (Canadian)',     continent: 'north-america', animalClass: 'large',  keMinFtLb: 80, momentumMin: 0.65, source: 'P&Y' },
  { name: 'Moose (Alaska-Yukon)', continent: 'north-america', animalClass: 'large',  keMinFtLb: 85, momentumMin: 0.70, source: 'P&Y · ADFG' },
  { name: 'Bison',                continent: 'north-america', animalClass: 'thick-skinned', keMinFtLb: 95, momentumMin: 0.80, source: 'P&Y · APHIS' },
  { name: 'Musk ox',              continent: 'north-america', animalClass: 'thick-skinned', keMinFtLb: 80, momentumMin: 0.65, source: 'P&Y · ADFG' },

  // Dangerous
  { name: 'Brown / Grizzly bear', continent: 'north-america', animalClass: 'dangerous', keMinFtLb: 80, momentumMin: 0.70, source: 'P&Y · ADFG' },
  { name: 'Polar bear',           continent: 'north-america', animalClass: 'dangerous', keMinFtLb: 85, momentumMin: 0.75, source: 'SCI · GN' },
  { name: 'Cougar',               continent: 'north-america', animalClass: 'medium',    keMinFtLb: 35, momentumMin: 0.40, source: 'P&Y · CPW' },

  // ───────────────────────────────────────────────────────────────────────
  // AFRICA — the "Africa 28"
  // ───────────────────────────────────────────────────────────────────────
  // Small antelope
  { name: 'Duiker (blue/common)', continent: 'africa', animalClass: 'small', keMinFtLb: 20, momentumMin: 0.30, source: 'SCI plains' },
  { name: 'Steenbok',             continent: 'africa', animalClass: 'small', keMinFtLb: 20, momentumMin: 0.30, source: 'SCI plains' },
  { name: 'Klipspringer',         continent: 'africa', animalClass: 'small', keMinFtLb: 20, momentumMin: 0.30, source: 'SCI plains' },
  { name: 'Springbok',            continent: 'africa', animalClass: 'small', keMinFtLb: 25, momentumMin: 0.35, source: 'SCI plains' },

  // Medium plains game
  { name: 'Impala',               continent: 'africa', animalClass: 'medium', keMinFtLb: 35, momentumMin: 0.40, source: 'SCI plains' },
  { name: 'Blesbok',              continent: 'africa', animalClass: 'medium', keMinFtLb: 35, momentumMin: 0.40, source: 'SCI plains' },
  { name: 'Bushbuck',             continent: 'africa', animalClass: 'medium', keMinFtLb: 35, momentumMin: 0.40, source: 'SCI plains' },
  { name: 'Reedbuck',             continent: 'africa', animalClass: 'medium', keMinFtLb: 35, momentumMin: 0.40, source: 'SCI plains' },
  { name: 'Warthog',              continent: 'africa', animalClass: 'medium', keMinFtLb: 50, momentumMin: 0.50, source: 'SCI plains · shield' },
  { name: 'Nyala',                continent: 'africa', animalClass: 'medium', keMinFtLb: 50, momentumMin: 0.50, source: 'SCI plains' },
  { name: 'Lechwe',               continent: 'africa', animalClass: 'medium', keMinFtLb: 45, momentumMin: 0.45, source: 'SCI plains' },
  { name: 'Tsessebe',             continent: 'africa', animalClass: 'medium', keMinFtLb: 50, momentumMin: 0.50, source: 'SCI plains' },

  // Large plains game
  { name: 'Kudu (greater)',       continent: 'africa', animalClass: 'large', keMinFtLb: 65, momentumMin: 0.55, source: 'SCI plains' },
  { name: 'Gemsbok / Oryx',       continent: 'africa', animalClass: 'large', keMinFtLb: 65, momentumMin: 0.55, source: 'SCI plains' },
  { name: 'Wildebeest (blue)',    continent: 'africa', animalClass: 'large', keMinFtLb: 70, momentumMin: 0.60, source: 'SCI · tough shoulder' },
  { name: 'Wildebeest (black)',   continent: 'africa', animalClass: 'large', keMinFtLb: 70, momentumMin: 0.60, source: 'SCI plains' },
  { name: 'Hartebeest (red)',     continent: 'africa', animalClass: 'large', keMinFtLb: 65, momentumMin: 0.55, source: 'SCI plains' },
  { name: 'Sable antelope',       continent: 'africa', animalClass: 'large', keMinFtLb: 70, momentumMin: 0.60, source: 'SCI plains' },
  { name: 'Roan antelope',        continent: 'africa', animalClass: 'large', keMinFtLb: 70, momentumMin: 0.60, source: 'SCI plains' },
  { name: 'Eland (common)',       continent: 'africa', animalClass: 'large', keMinFtLb: 85, momentumMin: 0.70, source: 'SCI · 2000 lb body' },
  { name: 'Zebra (Burchell)',     continent: 'africa', animalClass: 'large', keMinFtLb: 75, momentumMin: 0.65, source: 'SCI · tough hide' },
  { name: 'Zebra (Hartmann)',     continent: 'africa', animalClass: 'large', keMinFtLb: 75, momentumMin: 0.65, source: 'SCI plains' },

  // Dangerous / thick-skinned ("Big Five" archery-eligible)
  { name: 'Crocodile (Nile)',     continent: 'africa', animalClass: 'thick-skinned', keMinFtLb: 80, momentumMin: 0.70, source: 'SCI · armored' },
  { name: 'Hippo (land)',         continent: 'africa', animalClass: 'thick-skinned', keMinFtLb: 100, momentumMin: 0.85, source: 'SCI dangerous · 3500 lb' },
  { name: 'Cape buffalo',         continent: 'africa', animalClass: 'dangerous',     keMinFtLb: 100, momentumMin: 0.85, source: 'SCI dangerous · boss shield' },
  { name: 'Giraffe',              continent: 'africa', animalClass: 'large',         keMinFtLb: 85, momentumMin: 0.75, source: 'SCI · 2000+ lb' },
  { name: 'Lion',                 continent: 'africa', animalClass: 'dangerous',     keMinFtLb: 80, momentumMin: 0.70, source: 'SCI dangerous · charge risk' },
  { name: 'Leopard',              continent: 'africa', animalClass: 'dangerous',     keMinFtLb: 55, momentumMin: 0.55, source: 'SCI dangerous · thin skin, charge risk' },
  { name: 'Elephant',             continent: 'africa', animalClass: 'dangerous',     keMinFtLb: 120, momentumMin: 1.00, source: 'SCI · brain/heart shot only' },
  { name: 'Rhino (white)',        continent: 'africa', animalClass: 'thick-skinned', keMinFtLb: 110, momentumMin: 0.95, source: 'SCI · permits + tranquilizer use limited' },
];

// ─────────────────────────────────────────────────────────────────────────
// Verdict evaluation
// ─────────────────────────────────────────────────────────────────────────

export type AnimalVerdictTier = 'pass' | 'warn' | 'fail';

export interface AnimalVerdict {
  animal: Animal;
  tier: AnimalVerdictTier;
  /** Short summary: "75.8 ft·lb > 65 required" / "13.2 ft·lb short" */
  summary: string;
}

/**
 * Evaluate one animal vs. the current build's KE + momentum.
 * - pass  : both meet threshold by 10%+
 * - warn  : within 10% of threshold (still legal but marginal)
 * - fail  : either metric below threshold
 */
export function evaluateAnimal(animal: Animal, keFtLb: number, momentumSlugFps: number): AnimalVerdict {
  const keOk = keFtLb >= animal.keMinFtLb;
  const momOk = momentumSlugFps >= animal.momentumMin;
  const keMargin = (keFtLb - animal.keMinFtLb) / animal.keMinFtLb;
  const momMargin = (momentumSlugFps - animal.momentumMin) / animal.momentumMin;
  const worstMargin = Math.min(keMargin, momMargin);

  if (!keOk && !momOk) {
    const keShort = animal.keMinFtLb - keFtLb;
    const momShort = animal.momentumMin - momentumSlugFps;
    return {
      animal,
      tier: 'fail',
      summary: `−${keShort.toFixed(0)} ft·lb · −${momShort.toFixed(2)} momentum short`,
    };
  }
  if (!keOk) {
    return {
      animal,
      tier: 'fail',
      summary: `KE short by ${(animal.keMinFtLb - keFtLb).toFixed(1)} ft·lb`,
    };
  }
  if (!momOk) {
    return {
      animal,
      tier: 'fail',
      summary: `Momentum short by ${(animal.momentumMin - momentumSlugFps).toFixed(2)}`,
    };
  }
  if (worstMargin < 0.10) {
    return {
      animal,
      tier: 'warn',
      summary: `marginal · within 10% of min (${animal.keMinFtLb} ft·lb / ${animal.momentumMin.toFixed(2)} mom)`,
    };
  }
  return {
    animal,
    tier: 'pass',
    summary: `${keFtLb.toFixed(1)} ft·lb · ${momentumSlugFps.toFixed(2)} mom`,
  };
}

/**
 * Bulk evaluate, returning verdicts grouped by tier for quick UI rendering.
 */
export function evaluateAnimalSelection(
  selectedAnimals: Animal[],
  keFtLb: number,
  momentumSlugFps: number,
): {
  pass: AnimalVerdict[];
  warn: AnimalVerdict[];
  fail: AnimalVerdict[];
} {
  const verdicts = selectedAnimals.map((a) => evaluateAnimal(a, keFtLb, momentumSlugFps));
  return {
    pass: verdicts.filter((v) => v.tier === 'pass'),
    warn: verdicts.filter((v) => v.tier === 'warn'),
    fail: verdicts.filter((v) => v.tier === 'fail'),
  };
}

/**
 * "How many of the catalog am I cleared for right now?" — used by the
 * compact CTA card that opens the modal.
 */
export function clearedAnimalCount(keFtLb: number, momentumSlugFps: number): number {
  return ANIMALS.filter((a) => keFtLb >= a.keMinFtLb && momentumSlugFps >= a.momentumMin).length;
}

// ───────────────────────────────────────────────────────────────────────────
// VITAL-ZONE SIZE (for the Kill-Zone / ranging-forgiveness target).
//
// Vertical heart-lung diameter, inches — the number that matters for ranging
// error (a mis-ranged shot moves the impact vertically). These are bowhunting
// kill-zone ESTIMATES (broadside, behind the shoulder), not exact anatomy.
// Class fallback covers any species without an explicit value.
// ───────────────────────────────────────────────────────────────────────────
export const VITAL_HEIGHT_IN_BY_CLASS: Record<AnimalClass, number> = {
  small: 6,
  medium: 9,
  large: 13,
  'thick-skinned': 16,
  dangerous: 13,
};

const VITAL_HEIGHT_OVERRIDES: Record<string, number> = {
  // North America
  'Wild turkey': 4,
  Bobcat: 5,
  Coyote: 6,
  Javelina: 6,
  'Coues whitetail': 7,
  'Pronghorn antelope': 8,
  Wolf: 8,
  Cougar: 8,
  'Whitetail deer': 9,
  'Sitka blacktail': 8,
  'Columbia blacktail': 9,
  'Mule deer': 10,
  'Mountain goat': 9,
  'Bighorn sheep': 10,
  'Desert sheep': 10,
  'Black bear': 9,
  'Caribou (mountain)': 12,
  'Caribou (woodland)': 12,
  'Caribou (barren-ground)': 12,
  'Caribou (Quebec-Labrador)': 12,
  'Caribou (central Canada)': 12,
  Elk: 15,
  'Moose (Shiras)': 16,
  'Moose (Canadian)': 17,
  'Moose (Alaska-Yukon)': 18,
  Bison: 16,
  'Musk ox': 14,
  'Brown / Grizzly bear': 12,
  'Polar bear': 13,
  // Africa
  'Duiker (blue/common)': 4,
  Steenbok: 4,
  Klipspringer: 4,
  Springbok: 6,
  Impala: 8,
  Blesbok: 8,
  Bushbuck: 7,
  Reedbuck: 8,
  Warthog: 8,
  Nyala: 9,
  Lechwe: 9,
  Tsessebe: 10,
  'Hartebeest (red)': 11,
  'Gemsbok / Oryx': 12,
  'Sable antelope': 12,
  'Kudu (greater)': 13,
  'Roan antelope': 13,
  'Wildebeest (blue)': 13,
  'Wildebeest (black)': 12,
  'Eland (common)': 17,
  'Zebra (Burchell)': 14,
  'Zebra (Hartmann)': 13,
  Lion: 10,
  Leopard: 8,
  'Cape buffalo': 16,
  'Hippo (land)': 16,
  'Rhino (white)': 16,
  Giraffe: 18,
  Elephant: 18,
  'Crocodile (Nile)': 4,
};

/** Vital-zone vertical height (inches) for an animal. Override → class default. */
export function vitalHeightInches(a: Animal): number {
  return VITAL_HEIGHT_OVERRIDES[a.name] ?? VITAL_HEIGHT_IN_BY_CLASS[a.animalClass];
}

// ───────────────────────────────────────────────────────────────────────────
// VITAL-ZONE WIDTH (v154 — elliptical vital for the confidence engine).
//
// Broadside, the heart-lung area is WIDER than it is tall — the lungs extend
// rearward behind the shoulder. So the kill zone is an ellipse (width > height),
// which is why a horizontal miss is more forgiving than a vertical one. These
// widths are bowhunting ESTIMATES derived from the vertical height by a modest
// class ratio (lungs extend back ~1.15–1.3×), with explicit overrides where the
// broadside profile is notably round (turkey, croc). Under-promise: ratios are
// kept conservative. Used only by the confidence engine; the Kill-Zone target
// art still keys off height for the ranging-error (vertical) story.
// ───────────────────────────────────────────────────────────────────────────
const VITAL_WIDTH_RATIO_BY_CLASS: Record<AnimalClass, number> = {
  small: 1.25,
  medium: 1.3,
  large: 1.25,
  'thick-skinned': 1.25,
  dangerous: 1.15, // bears/big cats: rounder, deeper-than-wide chest
};

const VITAL_WIDTH_OVERRIDES: Record<string, number> = {
  // Broadside vital is small + roughly round, not elongated.
  'Wild turkey': 4,
  'Crocodile (Nile)': 5,
};

/**
 * Vital-zone horizontal width (inches) for an animal — the broadside
 * heart-lung extent. Override → height × class ratio (rounded). Always ≥ the
 * vertical height.
 */
export function vitalWidthInches(a: Animal): number {
  const override = VITAL_WIDTH_OVERRIDES[a.name];
  if (override !== undefined) return override;
  const h = vitalHeightInches(a);
  return Math.round(h * VITAL_WIDTH_RATIO_BY_CLASS[a.animalClass]);
}
